import {
  Box,
  Text,
  Skeleton,
  Progress,
} from "@salestrak/ui";
import { GhostState } from "./GhostState";
import { Section } from "./Section";
import { InfoTooltip } from "./InfoTooltip";
import type { AgeReasonMetrics as AgeReasonMetricsData, DashboardLabels } from "#app/lib/data";

const GHOST_ROW_WIDTHS = ["75%", "55%", "85%", "40%"] as const;

interface AgeReasonMetricsProps {
  data: AgeReasonMetricsData | null | undefined;
  labels: DashboardLabels | null | undefined;
  onClick: () => void;
}

export function AgeReasonMetrics({
  data,
  labels,
  onClick,
}: AgeReasonMetricsProps) {
  const loading = !data;

  const hasContactRate = loading || data.contactRate != null;
  const hasTargetMetricRate = loading || data.inspectionRate != null;
  const hasAnyRate = hasContactRate || hasTargetMetricRate;

  return (
    <Section
      title="Age Reason Metrics"
      titleExtra={
        <InfoTooltip>
          {labels?.contactVerb
            ? `Contact Rate = ${labels.contactVerb}s per contact (lower is better). `
            : ""}
          {labels?.targetMetric
            ? `${labels.targetMetric} Rate = contacts needed per ${labels.targetMetric}. `
            : ""}
          Tap to see per-rep breakdown.
        </InfoTooltip>
      }
      onClick={loading || data.total === 0 ? undefined : onClick}
    >
      <Box>
        <Box direction="column" gap="sm" fill="width">
          {hasAnyRate && (
            <>
              <Box direction="row" gap="sm" align="stretch">
                {hasContactRate && (
                  <Box fill="flex" rounded="lg" surface="sunken" padding="sm" border>
                    {loading ? (
                      <Skeleton className="h-18 w-full" />
                    ) : (
                      <>
                        <Text size="2xl" weight="bold">
                          {`${Math.round(data.contactRate!)}:1`}
                        </Text>
                        <Text size="xs" variant="muted">
                          Contact Rate
                        </Text>
                        <Text size="2xs" variant="muted">
                          {labels?.contactVerb
                            ? `${labels.contactVerb.charAt(0).toUpperCase()}${labels.contactVerb.slice(1)}s`
                            : ""}{" "}
                          per contact
                        </Text>
                      </>
                    )}
                  </Box>
                )}
                {hasTargetMetricRate && (
                  <Box fill="flex" rounded="lg" surface="sunken" padding="sm" border>
                    {loading ? (
                      <Skeleton className="h-18 w-full" />
                    ) : (
                      <>
                        <Text size="2xl" weight="bold">
                          {`${Math.round(data.inspectionRate!)}:1`}
                        </Text>
                        <Text size="xs" variant="muted">
                          {labels?.targetMetric} Rate
                        </Text>
                        <Text size="2xs" variant="muted">
                          Contacts per {labels?.targetMetric?.toLowerCase()}
                        </Text>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              <Box grow />
            </>
          )}

          <Box direction="column" gap="sm">
            {loading
              ? Array.from({ length: 4 }, (_, i) => (
                  <Box direction="column" key={i} gap="xs">
                    <Box direction="row" justify="between">
                      <Skeleton className="h-3 w-full" />
                    </Box>
                    <Skeleton className="h-3 w-full" />
                  </Box>
                ))
              : data.total === 0
                ? (
                    <GhostState overlay={<Text size="sm" weight="medium">No data for this period</Text>}>
                      <Box direction="column" gap="sm">
                        {GHOST_ROW_WIDTHS.map((w, i) => (
                          <Box direction="column" key={i} gap="xs">
                            <Box direction="row" justify="between">
                              <Box background="muted" height={10} width={w} rounded="sm" />
                              <Box background="muted" height={10} width="15%" rounded="sm" />
                            </Box>
                            <Box background="muted" height={8} width={w} rounded="sm" />
                          </Box>
                        ))}
                      </Box>
                    </GhostState>
                  )
                : data.reasons.map((r, i) => (
                    <Box direction="column" key={r.name} gap="xs">
                      <Box direction="row" justify="between">
                        <Text size="xs">{r.name}</Text>
                        <Text size="xs" weight="semibold">
                          {r.count}{" "}
                          <Text size="xs" variant="muted" weight="normal">
                            ({Math.round(r.pct)}%)
                          </Text>
                        </Text>
                      </Box>
                      <Progress value={r.pct} />
                    </Box>
                  ))}
          </Box>
        </Box>
      </Box>
    </Section>
  );
}
